import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

function SearchableDropdown({ options = [], value = "", onChange, placeholder = "Select a course..." }) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const dropdownRef = useRef(null);

    // Sync search input with value from parent
    useEffect(() => {
        setSearch(value || "");
    }, [value]);

    // Handle outside clicks to close the dropdown
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                // Revert search state to actual selected value on close
                setSearch(value || "");
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [value]);

    const filteredOptions = options.filter(option =>
        (option || "").toLowerCase().includes((search || "").toLowerCase())
    );

    const handleSelect = (option) => {
        onChange(option);
        setIsOpen(false);
    };

    return (
        <div className="relative w-full" ref={dropdownRef}>
            <div className="relative flex items-center w-full">
                <input
                    type="text"
                    className="w-full text-black rounded-md p-2 pr-10 outline-theme-yellow-dark border border-theme-dark bg-white text-sm"
                    placeholder={placeholder}
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setIsOpen(true);
                    }}
                    onFocus={() => setIsOpen(true)}
                />
                <button
                    type="button"
                    onClick={() => setIsOpen(!isOpen)}
                    className="absolute right-0 top-0 bottom-0 px-3 text-gray-500 hover:text-black focus:outline-none"
                >
                    <i className={`fa-solid ${isOpen ? "fa-chevron-up" : "fa-chevron-down"}`}></i>
                </button>
            </div>

            {isOpen && (
                <ul className="absolute z-50 w-full mt-1 max-h-60 overflow-y-auto bg-white border border-gray-300 rounded-md shadow-lg scrollbar-thin scrollbar-thumb-gray-300">
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((option, idx) => (
                            <li
                                key={idx}
                                onClick={() => handleSelect(option)}
                                className={`px-3 py-2 text-sm cursor-pointer text-black hover:bg-indigo-50 hover:text-indigo-900 transition-colors ${
                                    option === value ? "bg-indigo-100 font-semibold" : ""
                                }`}
                            >
                                {option}
                            </li>
                        ))
                    ) : (
                        <li className="px-3 py-2 text-sm text-gray-500 italic">No courses found</li>
                    )}
                </ul>
            )}
        </div>
    );
}

SearchableDropdown.propTypes = {
    options: PropTypes.arrayOf(PropTypes.string),
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string
};

export default SearchableDropdown;
